import { useEffect, useRef, useState } from 'react';
import { loadKakaoMaps } from '../../lib/kakaoMaps';
import { tCalendar } from '../../lib/calendar-i18n';
import { attachMapGestures, fitWhenShown } from './lib/mapGestures';
import { MapZoomButtons } from './MapZoomButtons';
import './LocationPicker.css';
import './MapControls.css';

/**
 * 장소 한 곳을 보여 주는 미리보기 지도(핀 하나) — 끌고 확대할 수 있다(2026-11-02 · 종전에는 정지 그림).
 * 일정 상세와 작성 화면의 장소 고르개가 함께 쓴다. 손놀림 규칙은 `lib/mapGestures.ts`.
 *
 * ⚠ 2026-09-29 — `LocationPicker.tsx` 안에 있던 것을 **글자 그대로** 이 파일로 옮겼다.
 *   까닭: 일정 «상세»에서도 같은 것을 쓰는데, 상세 모달이 장소 «고르개» 모듈을 통째로
 *   끌어오는 것은 이상하다. 고르개는 이제 이 파일을 불러 쓴다 — 그리는 것은 한 글자도 안 바뀌었다.
 *   (CSS 클래스도 그대로라 고르개 화면은 종전과 같다.)
 */
export function LocationPreviewMap({ pos }: { pos: { lat: number; lng: number } }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [map, setMap] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    let detachGestures: (() => void) | undefined;
    let detachFit: (() => void) | undefined;
    setStatus('loading');
    setMap(null);
    loadKakaoMaps()
      .then(() => {
        const root = rootRef.current;
        if (cancelled || !containerRef.current || !root) return;
        const { kakao } = window;
        const center = new kakao.maps.LatLng(pos.lat, pos.lng);
        const map = new kakao.maps.Map(containerRef.current, { center, level: 4 });
        detachGestures = attachMapGestures(map, root);
        new kakao.maps.CustomOverlay({
          map,
          position: center,
          yAnchor: 1,
          content: '<div class="travel-map-pin travel-map-pin--dest"></div>',
        });
        detachFit = fitWhenShown(map, containerRef.current, () => map.setCenter(center));
        setMap(map);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; detachGestures?.(); detachFit?.(); };
  }, [pos.lat, pos.lng]);

  return (
    <div ref={rootRef} className="calendar-location-picker-map">
      <div ref={containerRef} className="calendar-location-picker-map-canvas" />
      {map && <MapZoomButtons map={map} />}
      {status === 'error' && (
        <div className="travel-map-fallback">{tCalendar('calendar.event_create.location_map_load_failed')}</div>
      )}
    </div>
  );
}
